import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const menuItemClass =
  "block w-full rounded-[var(--radius-md)] px-2.5 py-1.5 text-left text-sm text-[var(--color-foreground)] no-underline transition-colors hover:bg-[var(--color-background)] disabled:cursor-not-allowed disabled:opacity-50";

export type DeskDocCreateMenuItem = {
  id: string;
  label: string;
  /** Prefill child route — plan/35 */
  to?: string;
  onSelect?: () => void;
  disabled?: boolean;
  /** Shown when disabled */
  title?: string;
};

/**
 * Create ▾ — state-aware child / follow-on docs on DeskDocActionRail (plan/35).
 * Omit the control when `items` is empty.
 */
export function DeskDocCreateMenu(props: {
  items: DeskDocCreateMenuItem[];
  disabled?: boolean;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const items = props.items;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div ref={ref} className={cn("relative", props.className)}>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={props.disabled}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        {props.label ?? "Create"}
        <span className="ml-1 text-[var(--color-muted)]" aria-hidden>
          ▾
        </span>
      </Button>
      {open ? (
        <div
          className="absolute left-0 top-full z-30 mt-1 min-w-[12rem] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-[var(--shadow-soft)]"
          role="menu"
          aria-label={props.label ?? "Create"}
        >
          {items.map((item) => {
            const close = () => setOpen(false);
            if (item.to && !item.disabled) {
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  role="menuitem"
                  className={menuItemClass}
                  title={item.title}
                  onClick={close}
                >
                  {item.label}
                </Link>
              );
            }
            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={menuItemClass}
                disabled={item.disabled}
                title={item.title}
                onClick={() => {
                  if (item.disabled) return;
                  item.onSelect?.();
                  close();
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
